/**
 * "Gotowe" and "Później" — the home's only write.
 *
 * `POST /attention-queue/responses` records one response against one occurrence (R6, R7). Done
 * suppresses it; Snoozed suppresses it until the named instant passes, after which it returns if
 * its condition still holds. Repeating a response has no additional effect, so a double press is
 * harmless and needs no guard of its own.
 *
 * ── It is available on EVERY card, including the ones the web cannot act on ──────────────────
 * Owner, 15 September 2026. A response is a statement about the trainer's own list (R4) and not
 * about the module the item came from, so a chat item a web trainer cannot answer is still one they
 * can clear off their morning. The primary is greyed; these two are not.
 *
 * ── The row leaves at once, and the read is the truth a moment later ─────────────────────────
 * The optimistic update drops the item from every cached queue answer — every ZONE's, because the
 * key carries one at its tail and the fact is true in all of them — and `onSettled` invalidates the
 * prefix so the server's own answer replaces it either way. On a refusal the snapshot goes back, so
 * a failed write leaves the trainer looking at the task they still have rather than at a list that
 * quietly disagrees with the server.
 *
 * WHICH item leaves is `model/attentionResponse`'s decision, and it needs the reason as well as the
 * anchor — two reasons share a weekly anchor. See that module.
 *
 * ── Every outcome is said out loud, and a refusal is also drawn ──────────────────────────────
 * The row vanishing is the only thing a sighted trainer saw, and nothing at all reached a screen
 * reader. So the host passes in its live region's `announce` and every settled write speaks: the
 * person and what was recorded about them, or the refusal. The refusal is ALSO drawn as ink, by the
 * card that asked for it, because "the row came back" is a change three rows down a scrolling card
 * and is not a report. Which WORDS those are is `lib/useQueueCard`'s — a refusal has one sentence
 * and it was being handed out from here as a constant field on every render.
 *
 * ── What comes back is the SETTLED outcome, and it names the row it was about ─────────────────
 * `mutation.isError` was not usable for either job. It is latched — it stays true until the next
 * write starts — and it says nothing about WHICH row was refused, while one responder serves both
 * bento cards. A card asking "was the write I am waiting on refused?" got "some write was, at some
 * point", which is how the refusal landing came to be unreachable: the card acted on the optimistic
 * removal, marked its landing spent, and the 500 that put the row back arrived to a card that had
 * already finished.
 *
 * `outcome` is a fresh object per SETTLEMENT, `null` again the moment a new write starts, and it
 * carries the row's own key. A card can therefore tell "mine, and it is decided" from "not mine"
 * and from "not yet".
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  attentionQueueQueryKey,
  attentionReasonWireValue,
  type AttentionReason,
} from '~/entities/client';
import { postAttentionQueueResponses } from '~/shared/api/generated/endpoints/attention-queue/attention-queue';
import type { TrainWithMeModulesAttentionDtosResponsesAttentionQueueResponse as AttentionQueueResponse } from '~/shared/api/generated/model';

import { afterBatch, withoutResponded } from '../model/attentionResponse';

/** The two answers this screen offers. The wire's `kind` is the C# enum member name. */
export type AttentionResponseKind = 'Done' | 'Snoozed';

export interface AttentionResponseCommand {
  /** The row's own key, `<reason>|<anchorKey>`, so a settled outcome can name the row it was about. */
  readonly key: string;
  readonly anchorKey: string;
  readonly reason: AttentionReason;
  readonly kind: AttentionResponseKind;
  /** The item's subject, or `null` for the pending invitation, whose invitee is not a client yet. */
  readonly subjectCustomerId: number | null;
  /** Who the row was about, for the sentence the live region reads out. */
  readonly task: string;
  /**
   * The snooze's term, worded, or `null` to say the default sentence.
   *
   * R6 is about a CHOSEN time, and the live region used to say "Odłożone do jutra" whichever of the
   * four terms was picked — so a reader who cannot see the menu was told tomorrow while the POST
   * carried next Monday. A surface that offers a choice passes the words for the term it sent.
   *
   * `null` is the Pulpit, which offers no choice and writes the default term: it keeps the sentence
   * it has always said, which its own specs pin.
   */
  readonly snoozeTerm?: string | null;
  /**
   * When a snoozed item comes back — the ISO instant the wire carries, or `null` for a `Done`.
   *
   * It is part of the COMMAND rather than computed in here, since 18 September 2026: `/zadania`
   * offers four terms (`model/snoozePresets`) and the Pulpit passes the default one, so the choice
   * belongs to the surface that made it and this hook stays about the write. R6 requires a future
   * instant; every preset is unconditionally one.
   */
  readonly snoozeUntilIso: string | null;
}

/**
 * A batch's identity — an object, used only for reference equality.
 *
 * Nothing is stored in it. What matters is that two batches are never the same object, so a write
 * knows which act it belongs to even while another one is in flight.
 */
type Batch = Record<string, never>;

/** What the mutation actually takes: one command, and the batch it was issued in, or `null`. */
interface Write {
  readonly command: AttentionResponseCommand;
  readonly batch: Batch | null;
}

/** How one write ended, and which row it was about. */
export interface AttentionResponseOutcome {
  /** `false` means the server refused it and the row is back where it was. */
  readonly ok: boolean;
  /** The answered row's `AttentionItem.key`. */
  readonly key: string;
}

export interface AttentionResponder {
  readonly respond: (command: AttentionResponseCommand) => void;
  /**
   * Record N responses as ONE act, and read the queue once when they have all settled.
   *
   * `POST /attention-queue/responses` has no batch form and no rate limit, but `GET
   * /attention-queue` is limited to 30 a minute — and the per-write `onSettled` below fires once
   * per write, so a bulk act over fourteen items was fourteen reads of a rate-limited endpoint.
   * Measured at SIX for six writes before this existed; that is what it fixes.
   *
   * `onRefused` is handed the keys the server turned down, all of them, because `outcome` carries
   * ONE row and the last settlement would otherwise be the only refusal a caller could see.
   */
  readonly respondMany: (
    commands: readonly AttentionResponseCommand[],
    onRefused: (keys: readonly string[]) => void,
  ) => void;
  /**
   * How the last write settled, or `null` while one is in flight and before the first.
   *
   * The cards wait on it: it is what tells a control that deleted itself whether to hand the
   * keyboard to the next row or back to the one that came back.
   */
  readonly outcome: AttentionResponseOutcome | null;
}

export interface AttentionResponseDeps {
  /** The host's live region. Every settled write says what it did through it. */
  readonly announce: (message: string) => void;
}

/**
 * Record one response.
 *
 * There is no pending flag: the row is gone from the list the instant `respond` is called, so there
 * is no control left to disable. What comes back instead is the refusal, which is the one outcome
 * the screen has to be able to draw.
 */
export function useAttentionResponse({ announce }: AttentionResponseDeps): AttentionResponder {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [outcome, setOutcome] = useState<AttentionResponseOutcome | null>(null);
  const mutation = useMutation({
    mutationFn: ({ command }: Write) =>
      postAttentionQueueResponses({
        anchorKey: command.anchorKey,
        kind: command.kind,
        reason: attentionReasonWireValue(command.reason),
        // Only a snooze carries one, and R6 requires it to be in the future. The term is the
        // caller's — see `snoozeUntilIso`.
        snoozeUntil: command.kind === 'Snoozed' ? (command.snoozeUntilIso ?? undefined) : undefined,
        subjectCustomerId: command.subjectCustomerId,
      }),
    onMutate: async ({ command, batch }) => {
      // Stop any read already in flight from landing on top of the optimistic list — it was issued
      // before the response and would put the row back for as long as it takes to invalidate.
      await queryClient.cancelQueries({ queryKey: attentionQueueQueryKey });
      // A BATCH snapshots ONCE, before it removes anything, and settles from that — see
      // `model/attentionResponse`'s `afterBatch` for what a per-write snapshot gets wrong.
      const snapshot =
        batch === null
          ? queryClient.getQueriesData<AttentionQueueResponse>({ queryKey: attentionQueueQueryKey })
          : null;
      queryClient.setQueriesData<AttentionQueueResponse>(
        { queryKey: attentionQueueQueryKey },
        (queue) => withoutResponded(queue, command),
      );
      return { snapshot };
    },
    onSuccess: (_data, { command, batch }) => {
      // A batch says what it did ONCE, in the caller's own words, and reports through its own
      // closing callback — so a member touches neither `outcome` nor the live region. Reporting
      // here as well announced a batch's refusal twice, and the first of them arrived before the
      // batch had finished settling, which is a refusal about a row nothing had put back yet.
      //
      // Membership is the write's OWN batch, not a shared slot: two batches can overlap, and a
      // shared slot let the second un-suppress the first one's writes still in flight.
      if (batch !== null) return;
      setOutcome({ ok: true, key: command.key });
      if (command.kind === 'Done') {
        announce(t('web:home.live.done', { task: command.task }));
        return;
      }
      const term = command.snoozeTerm ?? null;
      announce(
        term === null
          ? t('web:home.live.snoozed', { task: command.task })
          : t('web:home.live.snoozedUntil', { task: command.task, when: term }),
      );
    },
    onError: (_error, { command, batch }, context) => {
      // A batch rolls back, reports and announces as ONE act, in `respondMany`, from its own
      // pre-batch snapshot. See `onSuccess`.
      if (batch !== null) return;
      setOutcome({ ok: false, key: command.key });
      for (const [key, queue] of context?.snapshot ?? []) {
        queryClient.setQueryData(key, queue);
      }
      announce(t('web:home.live.failed'));
    },
    onSettled: (_data, _error, { batch }) => {
      // A batch invalidates once, at the end, in `respondMany`. See the read budget there.
      if (batch !== null) return;
      void queryClient.invalidateQueries({ queryKey: attentionQueueQueryKey });
    },
  });

  return {
    respond: (command) => {
      // Unsettled again, before anything is sent: a card must not read the PREVIOUS write's outcome
      // as this one's, which is what a latched `isError` did.
      setOutcome(null);
      mutation.mutate({ command, batch: null });
    },
    respondMany: (commands, onRefused) => {
      if (commands.length === 0) return;
      setOutcome(null);
      // The batch's own identity. Every write carries it, so its suppression is decided by the act
      // it was ISSUED in and a later batch cannot reach into an earlier one's writes.
      const batch: Batch = {};
      void (async () => {
        // ONE snapshot, before anything is removed. See `afterBatch`.
        await queryClient.cancelQueries({ queryKey: attentionQueueQueryKey });
        const before = queryClient.getQueriesData<AttentionQueueResponse>({
          queryKey: attentionQueueQueryKey,
        });
        const settled = await Promise.allSettled(
          commands.map((command) => mutation.mutateAsync({ command, batch })),
        );
        const accepted = commands.filter((_command, index) => settled[index]?.status === 'fulfilled');
        const refused = commands
          .filter((_command, index) => settled[index]?.status === 'rejected')
          .map((command) => command.key);

        if (refused.length > 0) {
          // Rebuilt from the pre-batch answer rather than from whichever rollback landed last: the
          // refused rows come back exactly where they were, and only the accepted ones stay gone.
          for (const [key, queue] of before) {
            queryClient.setQueryData(key, afterBatch(queue, accepted));
          }
          onRefused(refused);
          announce(t('web:home.live.failed'));
        }
        // ONE read, after the whole act.
        void queryClient.invalidateQueries({ queryKey: attentionQueueQueryKey });
      })();
    },
    outcome,
  };
}
